import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useMatches } from "react-router-dom";
import { Drawer, Spin } from "antd";
import { observer } from "mobx-react-lite";

import AppFooter, {
  type AppFooterVariant,
} from "../components/layout/AppFooter";
import AppHeader from "../components/layout/AppHeader";
import { SidebarMenu } from "../components/layout/SidebarMenu";
import { TopBanner } from "../components/layout/TopBanner";
import { EmailConfirmationModal } from "../components/profile/EmailConfirmationModal";
import { userStore } from "../stores/userStore";

import "./MainLayout.css";

interface RouteHandle {
  footerVariant?: AppFooterVariant;
  devMocked?: boolean;
}

const DEV_HOSTS = new Set(["localhost", "127.0.0.1", "my.nextgis.dev"]);

export const MainLayout = observer(function MainLayout() {
  const matches = useMatches();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const emailConfirmed = userStore.profile?.email_confirmed;
  const next = new URLSearchParams(location.search).get("next");
  const showJapaneseBackground = i18n.language.startsWith("ja");
  const isDevMockedRoute =
    DEV_HOSTS.has(window.location.hostname) &&
    matches.some(
      (match) => (match.handle as RouteHandle | undefined)?.devMocked,
    );

  useEffect(() => {
    if (!isDevMockedRoute) userStore.load();
  }, [isDevMockedRoute]);

  useEffect(() => {
    if (emailConfirmed && next && !next.startsWith("contact")) {
      window.location.href = next;
    }
  }, [emailConfirmed, next]);

  if (!isDevMockedRoute && userStore.authed === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!isDevMockedRoute && !userStore.authed) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login/1?next=${next}`} replace />;
  }

  const footerVariant = matches
    .map((m) => (m.handle as RouteHandle | undefined)?.footerVariant)
    .find(Boolean);

  return (
    <div className="main-layout">
      <TopBanner />
      <AppHeader onMenuClick={() => setDrawerOpen(true)} />
      <div
        className={`main-layout__body${showJapaneseBackground ? " main-layout__body--anime" : ""}`}
      >
        <SidebarMenu />
        <main className="main-layout__content">
          <Outlet />
        </main>
      </div>
      <AppFooter variant={footerVariant} />
      {userStore.profile?.email_confirmed === false && (
        <EmailConfirmationModal />
      )}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="left"
        styles={{ body: { padding: 0 }, wrapper: { width: 220 } }}
        title={null}
        closeIcon={null}
      >
        <SidebarMenu onNavigate={() => setDrawerOpen(false)} />
      </Drawer>
    </div>
  );
});
